import {Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {ActivatedRoute, Params} from '@angular/router';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {StringUtils} from '@application/helper/string-utils';
import {OverviewListMachineForDocumentation} from '@domain/machine/overview-list-machine-for-documentation';
import {MachinesDocumentation, MACHINES_DOCUMENTATION} from '@infrastructure/http/machines-documentation/machines-documentation';
import {MachineDocumentationOrderIdComponent} from '@presentation/pages/texbot/machine-documentation/machines-list/machine-documentation-order-id/machine-documentation-order-id.component';
import {BaseComponent, ColDefBuilderFactoryService, convertMachineOrderIdIfLegacy, GridOptionsBuilderFactoryService, TranslateService} from '@vdw/angular-component-library';
import {AgGridAngular} from 'ag-grid-angular';
import {ColDef, GridApi, GridOptions, IRowNode, ITooltipParams, RowClickedEvent, RowDataUpdatedEvent, ValueGetterParams} from 'ag-grid-community';
import {find, indexOf, isEmpty, isEqual, isNil} from 'lodash-es';
import {takeUntil} from 'rxjs/operators';

@Component({
  selector: 'app-machines-list',
  templateUrl: './machines-list.component.html'
})
export class MachinesListComponent extends BaseComponent implements OnInit, OnChanges {
  @ViewChild('machinesGrid') public machinesGrid: AgGridAngular;

  @Input() public machineId: number;
  @Input() public nameChanged: string;
  @Input() public gridIdentifier: GridIdentifier;
  @Output() public machineSelected: EventEmitter<OverviewListMachineForDocumentation> = new EventEmitter<OverviewListMachineForDocumentation>();
  public listOfMachines: OverviewListMachineForDocumentation[];
  public gridOptionsForMachines: GridOptions;

  private selectedMachine: OverviewListMachineForDocumentation;

  public constructor(
    @Inject(MACHINES_DOCUMENTATION) private readonly machinesDocumentation: MachinesDocumentation,
    private readonly translate: TranslateService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly gridOptionsBuilderFactoryService: GridOptionsBuilderFactoryService,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.initialiseGridOptionsForMachine();
    this.getMachinesAndSetSelectedMachineIfOrderIdIsProvided();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('nameChanged' in changes && !isNil(changes.nameChanged.currentValue)) {
      const nameChanged = changes.nameChanged.currentValue;
      const machineToUpdate = this.machinesGrid.api.getRowNode(nameChanged.id);
      const data = machineToUpdate.data;
      data.name = nameChanged.newName;
      this.machinesGrid.api.applyTransaction({update: [data]});
    }
  }

  public deselectMachine(): void {
    this.selectedMachine = null;
    this.machinesGrid.api.deselectAll();
  }

  private getMachinesAndSetSelectedMachineIfOrderIdIsProvided(): void {
    this.machinesDocumentation
      .getAll()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((listOfMachines: OverviewListMachineForDocumentation[]) => {
        this.listOfMachines = listOfMachines;

        this.setSelectedMachineIfOrderIdIsProvided();
      });
  }

  private setSelectedMachineIfOrderIdIsProvided(): void {
    this.activatedRoute.queryParams.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((queryParams: Params) => {
      if (!isEmpty(queryParams)) {
        const machineOrderId = convertMachineOrderIdIfLegacy(queryParams.orderId);

        this.selectedMachine = find(this.listOfMachines, {orderId: machineOrderId});
        if (!isNil(this.selectedMachine)) {
          this.setSelectedMachine();
          this.machinesGrid.api.ensureIndexVisible(indexOf(this.listOfMachines, this.selectedMachine));
        }
      }
    });
  }

  private initialiseGridOptionsForMachine(): void {
    this.gridOptionsForMachines = this.gridOptionsBuilderFactoryService
      .getBuilder(this.getColumnDefsForMachines(), GridIdentifier.MACHINE_LIST)
      .withOnRowClicked((rowClickedEvent: RowClickedEvent) => this.onRowClicked(rowClickedEvent))
      .withOnRowDataUpdated(({api}: RowDataUpdatedEvent) => {
        if (!isNil(this.machineId)) {
          const rowNodeOfSelectedMachine: IRowNode<OverviewListMachineForDocumentation> = api.getRowNode(`${this.machineId}`);

          if (!isNil(rowNodeOfSelectedMachine)) {
            rowNodeOfSelectedMachine.setSelected(true);
          }
        }
      })
      .withNoRowsOverlay({
        titleParam: 'MACHINE.MACHINE',
        hideDescription: true,
        scale: 0.8
      })
      .withLoadingOverlay()
      .build();
  }

  private onRowClicked(event: RowClickedEvent): void {
    if (event && event.node && event.node.data) {
      const machineOfRow: OverviewListMachineForDocumentation = event.node.data;
      if (event.node.isSelected() && !isEqual(this.selectedMachine, machineOfRow)) {
        this.selectedMachine = machineOfRow;

        this.setSelectedMachine();
      } else {
        this.deselectRow(event.api);
      }
    }
  }

  private deselectRow(gridApi: GridApi): void {
    this.selectedMachine = null;
    gridApi.deselectAll();

    this.setSelectedMachine();
  }

  private getMachineNameOrPlaceholder(params: ValueGetterParams | ITooltipParams): string {
    return StringUtils.getPropertyValueOrGeneralPlaceholderIfEmpty(params.data, 'name', this.translate);
  }

  private getColumnDefsForMachines(): ColDef[] {
    return [
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('name', true)
        .withHeaderName('GENERAL.NAME')
        .withComparator(StringUtils.stringComparator)
        .withValueGetter((params: ValueGetterParams) => this.getMachineNameOrPlaceholder(params), true)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('orderId', true)
        .withHeaderName('MACHINE.DETAILS.ORDER_ID')
        .withCellRenderer(MachineDocumentationOrderIdComponent)
        .withComparator(StringUtils.stringComparator)
        .build()
    ];
  }

  private setSelectedMachine(): void {
    this.machineSelected.emit(this.selectedMachine);
  }
}
